import React, { useState, useEffect, useCallback } from 'react';
import { User, Role, CreateUser, UpdateUser, UserAuditLog, PagedResult } from '../types/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Box,
} from '@mui/material';
import { apiService } from '../services/api';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import AuditLogViewer from './AuditLogViewer';
import { appColors } from '../theme/colors';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageTableSurface,
  ListPageContentSection,
  ListPageMobileStack,
  ListPageTableHeadRow,
  ListPageTh,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';

interface UsersTabProps {
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  onRegisterCreateAction?: (handler: (() => void) | null) => void;
}

const UsersTab: React.FC<UsersTabProps> = ({ onError, onSuccess, onRegisterCreateAction }) => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreateUser = hasPermission(user, permissions.userManagement.createUser);
  const canEditUser = hasPermission(user, permissions.userManagement.editUser);
  const canDeleteUser = hasPermission(user, permissions.userManagement.deleteUser);
  const [pagedResult, setPagedResult] = useState<PagedResult<User> | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortBy, setSortBy] = useState<string>('id');
  const [sortDescending, setSortDescending] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(0);
  const [userAuditLogs, setUserAuditLogs] = useState<UserAuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  
  const [formData, setFormData] = useState<CreateUser>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    roleId: 2, // Default to user role
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const filter = {
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: itemsPerPage,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      const result = await apiService.getUsers(filter);
      setPagedResult(result);
    } catch (err: any) {
      onError('Failed to fetch users. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onError, searchTerm, currentPage, itemsPerPage, sortBy, sortDescending]);

  const fetchRoles = useCallback(async () => {
    try {
      // Fetch all roles for dropdown (use large page size)
      const result = await apiService.getRoles({ pageSize: 1000 });
      setRoles(result.items);
    } catch (err: any) {
      console.error('Failed to fetch roles:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, [fetchUsers, fetchRoles]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleOpenDialog = (user?: User) => {
    setActiveTab(0); // Reset to Details tab
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        password: '',
        roleId: user.roleId,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        password: '',
        roleId: 2, // Default to user role
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingUser(null);
    setActiveTab(0);
    setUserAuditLogs([]); // Clear audit logs
    setFormData({
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      roleId: 2, // Default to user role
    });
  };

  const handleSubmit = async (event: React.MouseEvent) => {
    event.preventDefault();
    try {
      if (editingUser) {
        if (!canEditUser) {
          onError('You do not have permission to edit users.');
          return;
        }
        const updateData: UpdateUser = {
          username: formData.username,
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          roleId: formData.roleId,
        };
        await apiService.updateUser(editingUser.id, updateData);
        onSuccess('User updated successfully!');
      } else {
        if (!canCreateUser) {
          onError('You do not have permission to create users.');
          return;
        }
        await apiService.createUser(formData);
        onSuccess('User created successfully!');
      }

      handleCloseDialog();
      fetchUsers();
    } catch (err: any) {
      onError(err.response?.data?.message || 'Operation failed. Please try again.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!canDeleteUser) {
      onError('You do not have permission to delete users.');
      return;
    }
    const confirmed = await confirmDelete({ message: 'Are you sure you want to delete this user?' });
    if (!confirmed) return;
    try {
      await apiService.deleteUser(id);
      onSuccess('User deleted successfully!');
      fetchUsers();
    } catch (err: any) {
      onError(err.response?.data?.message || 'Failed to delete user.');
    }
  };

  useEffect(() => {
    onRegisterCreateAction?.(canCreateUser ? () => handleOpenDialog() : null);
    return () => onRegisterCreateAction?.(null);
  }, [onRegisterCreateAction, canCreateUser]);

  const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    
    // Lazy load audit logs when switching to Change History tab
    if (newValue === 1 && editingUser && userAuditLogs.length === 0) {
      setAuditLoading(true);
      try {
        const logs = await apiService.getUserAuditLogs(editingUser.id);
        setUserAuditLogs(logs);
      } catch (err: any) {
        onError('Failed to fetch audit logs');
      } finally {
        setAuditLoading(false);
      }
    }
  };

  const getRoleColor = (roleName: string) => {
    switch (roleName.toLowerCase()) {
      case 'admin':
        return 'text-blue-300 bg-blue-900/50';
      case 'editor':
        return 'text-purple-300 bg-purple-900/50';
      case 'viewer':
        return 'text-yellow-300 bg-yellow-900/50';
      default:
        return 'text-gray-300 bg-gray-900/50';
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive
      ? 'text-green-300 bg-green-900/50'
      : 'text-red-300 bg-red-900/50';
  };

  const getStatusDot = (isActive: boolean) => {
    return isActive ? 'bg-green-500' : 'bg-red-500';
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column: string) => {
    if (sortBy !== column) return 'unfold_more';
    return sortDescending ? 'arrow_downward' : 'arrow_upward';
  };

  const currentUsers = pagedResult?.items || [];
  const totalPages = pagedResult?.totalPages || 0;

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <CircularProgress />
      </div>
    );
  }

  return (
    <ListPageRoot>
      <ListPageToolbar className="flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <SearchBar
          variant="listPage"
          placeholder="Search users..."
          value={searchTerm}
          onChange={setSearchTerm}
          className="w-full sm:max-w-md"
        />
      </ListPageToolbar>

      <ListPageContentSection>
        <ListPageTableSurface>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <ListPageTableHeadRow>
                  <ListPageTh scope="col" className="cursor-pointer" onClick={() => handleSort('id')}>
                    <div className="flex items-center gap-1">
                      ID
                      <span className="material-symbols-outlined text-base">{getSortIcon('id')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh scope="col" className="cursor-pointer" onClick={() => handleSort('username')}>
                    <div className="flex items-center gap-1">
                      Username
                      <span className="material-symbols-outlined text-base">{getSortIcon('username')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh
                    scope="col"
                    className="cursor-pointer hidden lg:table-cell"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center gap-1">
                      Email
                      <span className="material-symbols-outlined text-base">{getSortIcon('email')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh
                    scope="col"
                    className="cursor-pointer hidden xl:table-cell"
                    onClick={() => handleSort('firstname')}
                  >
                    <div className="flex items-center gap-1">
                      First Name
                      <span className="material-symbols-outlined text-base">{getSortIcon('firstname')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh
                    scope="col"
                    className="cursor-pointer hidden xl:table-cell"
                    onClick={() => handleSort('lastname')}
                  >
                    <div className="flex items-center gap-1">
                      Last Name
                      <span className="material-symbols-outlined text-base">{getSortIcon('lastname')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh scope="col" className="text-center cursor-pointer" onClick={() => handleSort('role')}>
                    <div className="flex items-center justify-center gap-1">
                      Role
                      <span className="material-symbols-outlined text-base">{getSortIcon('role')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh
                    scope="col"
                    className="text-center cursor-pointer"
                    onClick={() => handleSort('isactive')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Status
                      <span className="material-symbols-outlined text-base">{getSortIcon('isactive')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh
                    scope="col"
                    className="cursor-pointer hidden lg:table-cell"
                    onClick={() => handleSort('createdat')}
                  >
                    <div className="flex items-center gap-1">
                      Created
                      <span className="material-symbols-outlined text-base">{getSortIcon('createdat')}</span>
                    </div>
                  </ListPageTh>
                  <ListPageTh scope="col" className="text-right">
                    Actions
                  </ListPageTh>
                </ListPageTableHeadRow>
              </thead>
              <tbody className={listPageTableBodyClass}>
                {currentUsers.map((user) => (
                  <tr key={user.id} className={listPageTableRowClass}>
                    <td className="px-5 py-4 text-slate-300">#{user.id}</td>
                    <td className="px-5 py-4 text-white font-medium">{user.username}</td>
                    <td className="px-5 py-4 text-slate-200 hidden lg:table-cell">{user.email}</td>
                    <td className="px-5 py-4 text-slate-200 hidden xl:table-cell">{user.firstName}</td>
                    <td className="px-5 py-4 text-slate-200 hidden xl:table-cell">{user.lastName}</td>
                    <td className="px-5 py-4 text-center">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.roleName)}`}>
                        {user.roleName}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(user.isActive)}`}>
                        <span className={`w-2 h-2 mr-2 rounded-full ${getStatusDot(user.isActive)}`} />
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-slate-300 hidden lg:table-cell">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="action-row-icon-buttons">
                        <button
                          type="button"
                          className="text-slate-300 hover:bg-white/5"
                          aria-label="View user"
                          onClick={() => {}}
                        >
                          <span className="material-symbols-outlined text-base">visibility</span>
                        </button>
                        {canEditUser && (
                          <button
                            type="button"
                            className="text-primary hover:bg-primary/10"
                            aria-label="Edit user"
                            onClick={() => handleOpenDialog(user)}
                          >
                            <span className="material-symbols-outlined text-base">edit</span>
                          </button>
                        )}
                        {canDeleteUser && (
                          <button
                            type="button"
                            className="text-red-400 hover:bg-red-500/10"
                            aria-label="Delete user"
                            onClick={() => handleDelete(user.id)}
                          >
                            <span className="material-symbols-outlined text-base">delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </ListPageTableSurface>

        <ListPageMobileStack>
          {currentUsers.map((user) => (
            <div
              key={user.id}
              className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="material-symbols-outlined text-base text-primary shrink-0">person</span>
                    <h3 className="text-white font-semibold text-lg">{user.username}</h3>
                    <span className="text-xs text-slate-500">#{user.id}</span>
                  </div>
                  <p className="text-slate-400 text-sm mb-2">{user.email}</p>
                  {(user.firstName || user.lastName) && (
                    <p className="text-slate-300 text-sm">
                      {user.firstName} {user.lastName}
                    </p>
                  )}
                </div>
                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full shrink-0 ${getStatusColor(user.isActive)}`}>
                  <span className={`w-2 h-2 mr-1 rounded-full ${getStatusDot(user.isActive)}`} />
                  {user.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm text-primary">security</span>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getRoleColor(user.roleName)}`}>
                    {user.roleName}
                  </span>
                </div>
                {user.createdAt && <span>{new Date(user.createdAt).toLocaleDateString()}</span>}
              </div>

              <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                <button type="button" className="text-slate-300 hover:bg-white/5" onClick={() => {}}>
                  <span className="material-symbols-outlined">visibility</span>
                </button>
                {canEditUser && (
                  <button
                    type="button"
                    className="text-primary hover:bg-primary/10"
                    onClick={() => handleOpenDialog(user)}
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                )}
                {canDeleteUser && (
                  <button
                    type="button"
                    className="text-red-400 hover:bg-red-500/10"
                    onClick={() => handleDelete(user.id)}
                  >
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </ListPageMobileStack>
      </ListPageContentSection>

      {totalPages > 1 && (
        <div className="px-4 lg:px-5 pb-4">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      )}

      {/* User Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md" 
        fullWidth
        PaperProps={listPageDialogPaperProps}
      >
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600, pb: 1 }}>
          {editingUser ? `Edit User - ${editingUser.username}` : 'Create New User'}
        </DialogTitle>
        
        {editingUser && (
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Details" />
              <Tab label="Change History" />
            </Tabs>
          </Box>
        )}
        
        <DialogContent sx={{ px: 3 }}>
          {activeTab === 0 ? (
            <div className="pt-1 space-y-4">
              <TextField
                autoFocus
                margin="dense"
                label="Username"
                fullWidth
                variant="outlined"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Email"
                type="email"
                fullWidth
                variant="outlined"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
              <div className="flex gap-4">
                <TextField
                  margin="dense"
                  label="First Name"
                  fullWidth
                  variant="outlined"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
                <TextField
                  margin="dense"
                  label="Last Name"
                  fullWidth
                  variant="outlined"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
              {!editingUser && (
                <TextField
                  margin="dense"
                  label="Password"
                  type="password"
                  fullWidth
                  variant="outlined"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                />
              )}
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.roleId}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, roleId: Number(e.target.value) })}
                >
                  {roles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleId}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          ) : (
            <AuditLogViewer auditLogs={userAuditLogs} loading={auditLoading} />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          {activeTab === 0 && (
            <>
              <Button 
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ 
                  color: appColors.mutedText, 
                  borderColor: appColors.dialogBorder,
                  '&:hover': {
                    borderColor: appColors.primary,
                    color: appColors.primary,
                  }
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmit} 
                variant="contained"
                sx={{
                  backgroundColor: appColors.primary,
                  '&:hover': {
                    backgroundColor: appColors.primary,
                    opacity: 0.9,
                  }
                }}
              >
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </>
          )}
          {activeTab === 1 && (
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{ 
                color: appColors.mutedText, 
                borderColor: appColors.dialogBorder,
                '&:hover': {
                  borderColor: appColors.primary,
                  color: appColors.primary,
                }
              }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </ListPageRoot>
  );
};

export default UsersTab;